import React from "react";

interface TableProps {
  columns: string[];
  columnHeaders: string[];
  data: Array<Record<string, any>>;
  setSelectedOrder: React.Dispatch<
    React.SetStateAction<{ id: number; status: string } | null>
  >;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  onViewClick?: (partId: number) => void;
  // New props for change quantity
  changeQuantities?: { [partId: number]: string };
  loadingRows?: { [partId: number]: boolean };
  successRows?: { [partId: number]: boolean };
  errorRows?: { [partId: number]: string };
  onChangeQuantityInput?: (partId: number, value: string) => void;
  onUpdateQuantity?: (
    partId: number,
    warehouseId: number,
    currentQuantity: number
  ) => void;
  selectedHub?: number | null; // Add selectedHub prop
}

const InventoryTableComponent: React.FC<TableProps> = ({
  columns = [],
  columnHeaders = [],
  data: initialData = [],
  setSelectedOrder,
  currentPage = 0,
  setCurrentPage,
  totalPages = 1,
  onViewClick,
  changeQuantities = {},
  loadingRows = {},
  successRows = {},
  errorRows = {},
  onChangeQuantityInput,
  onUpdateQuantity,
  selectedHub,
}) => {
  const handleViewClick = (partId: number) => {
    if (onViewClick) {
      onViewClick(partId);
    }
  };

  // Calculate the correct row number based on items per page (10)
  const getRowNumber = (index: number) => {
    return currentPage * 10 + index + 1;
  };

  // Ensure data is always an array
  const tableData = Array.isArray(initialData) ? initialData : [];

  return (
    <div className="w-full bg-white rounded-lg shadow-sm">
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="w-16 px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                #
              </th>
              {Array.isArray(columnHeaders) &&
                columnHeaders.map((header, index) => (
                  <th
                    key={index}
                    className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    {header}
                  </th>
                ))}
              <th className="w-24 px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {tableData.length > 0 ? (
              tableData.map((row, rowIndex) => {
                const rowNumber = getRowNumber(rowIndex);

                return (
                  <tr
                    key={row.partId || rowIndex}
                    className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {rowNumber}
                    </td>
                    {Array.isArray(columns) &&
                      columns.map((column, colIndex) => {
                        if (column === "changeQuantity") {
                          // Find warehouseId for this row (assuming row.warehouseId or row.hubId)
                          const warehouseId =
                            row.warehouseId ||
                            row.hubId ||
                            row.hubID ||
                            row.warehouseID ||
                            row.id ||
                            selectedHub; // fallback to selectedHub from props
                          return (
                            <td
                              key={colIndex}
                              className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="number"
                                  min="0"
                                  className="border rounded px-2 py-1 w-24 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  value={changeQuantities[row.partId] ?? ""}
                                  onChange={(e) => {
                                    // Prevent negative input
                                    const val = e.target.value;
                                    if (
                                      val === "" ||
                                      (!isNaN(Number(val)) && Number(val) >= 0)
                                    ) {
                                      onChangeQuantityInput &&
                                        onChangeQuantityInput(row.partId, val);
                                    }
                                  }}
                                  placeholder="New Qty"
                                  disabled={loadingRows[row.partId]}
                                />
                                <button
                                  className={`px-2 py-1 rounded text-xs font-medium transition-colors
                                    ${
                                      loadingRows[row.partId]
                                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        : "bg-green-600 text-white hover:bg-green-700"
                                    }
                                  `}
                                  disabled={
                                    loadingRows[row.partId] ||
                                    !changeQuantities[row.partId] ||
                                    isNaN(
                                      Number(changeQuantities[row.partId])
                                    ) ||
                                    Number(changeQuantities[row.partId]) ===
                                      row.quantityOnHand
                                  }
                                  onClick={() =>
                                    onUpdateQuantity &&
                                    onUpdateQuantity(
                                      row.partId,
                                      warehouseId,
                                      row.quantityOnHand
                                    )
                                  }>
                                  {loadingRows[row.partId] ? (
                                    <svg
                                      className="animate-spin h-4 w-4 text-white"
                                      viewBox="0 0 24 24">
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                        fill="none"
                                      />
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8v8z"
                                      />
                                    </svg>
                                  ) : successRows[row.partId] ? (
                                    <span className="text-green-700">✓</span>
                                  ) : (
                                    "Update"
                                  )}
                                </button>
                              </div>
                              {errorRows[row.partId] && (
                                <div className="text-xs text-red-600 mt-1">
                                  {errorRows[row.partId]}
                                </div>
                              )}
                            </td>
                          );
                        }
                        return (
                          <td
                            key={colIndex}
                            className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                            {column === "price" || column === "costPrice" ? (
                              <span>
                                ₹{" "}
                                {typeof row[column] === "number"
                                  ? row[column].toFixed(2)
                                  : parseFloat(row[column] || 0).toFixed(2)}
                              </span>
                            ) : column === "partId" ? (
                              <span className="font-medium">{row[column]}</span>
                            ) : column === "partName" ||
                              column === "partProductName" ? (
                              <span
                                className="truncate max-w-xs"
                                title={row[column]}>
                                {row[column] || "-"}
                              </span>
                            ) : (
                              <span>
                                {row[column] !== undefined &&
                                row[column] !== null
                                  ? row[column]?.toString()
                                  : "0"}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    <td className="px-6 py-4">
                      {row.partId && (
                        <button
                          onClick={() => handleViewClick(row.partId)}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 transition-colors">
                          View
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={(columnHeaders?.length || 0) + 2}
                  className="px-6 py-12 text-center text-gray-500">
                  <div className="flex flex-col items-center">
                    <svg
                      className="w-12 h-12 text-gray-300 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                      />
                    </svg>
                    <p className="text-lg font-medium text-gray-900 mb-1">
                      No products found
                    </p>
                    <p className="text-gray-500">
                      Try adjusting your search or filter criteria
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
          <div className="flex items-center space-x-2">
            {/* First Page Button */}
            <button
              onClick={() => setCurrentPage && setCurrentPage(0)}
              disabled={currentPage === 0}
              className={`px-3 py-2 text-xs font-medium rounded-md border ${
                currentPage === 0
                  ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
              }`}>
              First
            </button>

            {/* Previous Button */}
            <button
              onClick={() =>
                setCurrentPage &&
                setCurrentPage((prev) => Math.max(prev - 1, 0))
              }
              disabled={currentPage === 0}
              className={`p-2 rounded-md border ${
                currentPage === 0
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {/* Next Button */}
            <button
              onClick={() =>
                setCurrentPage &&
                setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))
              }
              disabled={currentPage >= totalPages - 1}
              className={`p-2 rounded-md border ${
                currentPage >= totalPages - 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            {/* Last Page Button */}
            <button
              onClick={() => setCurrentPage && setCurrentPage(totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
              className={`px-3 py-2 text-xs font-medium rounded-md border ${
                currentPage >= totalPages - 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
              }`}>
              Last
            </button>
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-700">
              Page {currentPage + 1} of {totalPages}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryTableComponent;
